import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { useUser } from '../../contexts/UserContext';
import { extractKeywords, generateCoverLetter } from '../../services/openaiService';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  MailIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SendIcon,
  XIcon,
  LinkIcon,
  SparklesIcon,
} from '../icons';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * Cover Letter — Electron-native pane.
 *
 * Two views:
 *   1. SetupView   — job + company + JD + resume + personal info.
 *                    Calls deduct-credit, extractKeywords, generateCoverLetter,
 *                    then saves to DB (best-effort).
 *   2. ResultsView — editable cover letter, keyword chip strip, copy / DOCX
 *                    download, edit form / start over.
 *
 * Backend endpoints (same as the website):
 *   POST /api/cover-letter/deduct-credit
 *   POST /api/cover-letter/save
 * ─────────────────────────────────────────────────────────────────────── */

async function extractText(file) {
  const name = (file.name || '').toLowerCase();
  if (file.type === 'text/plain' || name.endsWith('.txt')) return await file.text();
  if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const c = await page.getTextContent();
      out += c.items.map(it => it.str).join(' ') + '\n';
    }
    return out.trim();
  }
  if (name.endsWith('.docx') || name.endsWith('.doc') || file.type.includes('word')) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return (r.value || '').trim();
  }
  throw new Error('Unsupported file. Use PDF, DOCX, DOC, or TXT.');
}

function buildResumeTextFromProfile(profile) {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.length) {
    text += 'WORK EXPERIENCE\n';
    profile.workExperience.forEach(exp => {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `  ${exp.description}\n`;
      }
    });
    text += '\n';
  }
  if (Array.isArray(profile.education) && profile.education.length) {
    text += 'EDUCATION\n';
    profile.education.forEach(edu => {
      if (edu.degree || edu.institution) {
        text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
      }
    });
  }
  return text.trim();
}

export default function CoverLetterPane() {
  const [view, setView] = useState('setup'); // 'setup' | 'results'
  const [resultData, setResultData] = useState(null);
  // Hold form state at the parent so "Edit form" preserves it.
  const [formState, setFormState] = useState(null);

  if (view === 'setup') {
    return (
      <SetupView
        initialState={formState}
        onGenerated={(data) => {
          setResultData(data);
          setFormState(data.formState);
          setView('results');
        }}
      />
    );
  }
  return (
    <ResultsView
      data={resultData}
      onEdit={() => setView('setup')}
      onReset={() => {
        setView('setup');
        setFormState(null);
        setResultData(null);
      }}
      onUpdate={(coverLetter) =>
        setResultData(prev => ({ ...prev, coverLetter }))
      }
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* SetupView                                                               */
/* ─────────────────────────────────────────────────────────────────────── */

function SetupView({ initialState, onGenerated }) {
  const { fetchCredits, setCredits } = useUser();

  const [form, setForm] = useState(initialState?.form || {
    jobTitle: '',
    companyName: '',
    hiringManagerName: '',
    jobDescription: '',
  });
  const [personalInfo, setPersonalInfo] = useState(initialState?.personalInfo || {
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
  });
  const [resumeChoice, setResumeChoice] = useState(initialState?.resumeChoice || 'profile');
  const [uploadedFile, setUploadedFile] = useState(initialState?.uploadedFile || null);
  const [uploadedText, setUploadedText] = useState(initialState?.uploadedText || '');
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [profile, setProfile] = useState({ data: null, hasResume: false, loading: true });
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const updatePI = (k) => (e) => setPersonalInfo(p => ({ ...p, [k]: e.target.value }));

  // Pre-fill personal info from profile on first load (only if empty).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const p = d?.profile;
        const filename = p?.resumeFilename || null;
        setProfile({ data: p, hasResume: !!filename, loading: false });
        if (!filename && resumeChoice === 'profile') setResumeChoice('upload');
        // Pre-fill personal info ONLY when not previously populated.
        if (p && !initialState?.personalInfo) {
          setPersonalInfo(prev => ({
            fullName: prev.fullName || p.fullName || '',
            email:    prev.email    || p.email    || '',
            phone:    prev.phone    || p.phone    || '',
            location: prev.location || p.location || '',
            linkedIn: prev.linkedIn || p.linkedIn || '',
          }));
        }
      } catch {
        if (!cancelled) setProfile({ data: null, hasResume: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setParseError('');
    setIsParsing(true);
    try {
      const text = await extractText(file);
      if (!text) throw new Error('No text extracted from file.');
      setUploadedText(text);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
      setUploadedText('');
    } finally {
      setIsParsing(false);
    }
  };

  const canStart = (() => {
    if (!form.jobTitle.trim() || !form.jobDescription.trim()) return false;
    if (resumeChoice === 'upload' && !uploadedText) return false;
    if (resumeChoice === 'profile' && !profile.hasResume) return false;
    return true;
  })();

  const gate = useFeatureGate('coverLetter');

  const handleGenerate = async () => {
    if (!canStart) return;
    if (gate.locked) { gate.openPaywall(); return; }
    setGenerating(true);
    setError('');
    try {
      // 1) deduct credit via the shared gate
      const { ok, reason, remaining } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') { setGenerating(false); return; }
        throw new Error(reason || 'Failed to deduct credit');
      }
      if (setCredits && typeof remaining === 'number' && Number.isFinite(remaining)) {
        setCredits(prev => Math.max(0, (prev || 0) - 1));
      }

      const resumeText = resumeChoice === 'upload'
        ? uploadedText
        : buildResumeTextFromProfile(profile.data);

      // 2) extract keywords (used both for generation and chip display)
      let kws = [];
      try {
        const k = await extractKeywords(form.jobDescription.trim());
        kws = Array.isArray(k) ? k : [];
      } catch { /* keyword extraction is non-essential */ }

      // 3) generate the letter
      const generated = await generateCoverLetter(
        resumeText,
        form.jobDescription.trim(),
        kws,
        form.companyName.trim(),
        form.hiringManagerName.trim(),
        form.jobTitle.trim(),
        personalInfo,
      );
      if (!generated || !generated.trim()) {
        throw new Error('The AI returned an empty cover letter. Please try again.');
      }

      // 4) save (best-effort)
      const fileName = `Cover_Letter_${form.jobTitle.replace(/\s+/g, '_') || 'Position'}_${form.companyName.replace(/\s+/g, '_') || 'Company'}.docx`;
      fetch(`${API_BASE_URL}/api/cover-letter/save`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coverLetterContent: generated,
          jobTitle: form.jobTitle.trim(),
          companyName: form.companyName.trim(),
          jobDescription: form.jobDescription.trim(),
          keywords: kws,
          personalInfo,
          fileName,
        }),
      }).catch(() => {});

      if (fetchCredits) fetchCredits();

      onGenerated({
        coverLetter: generated,
        keywords: kws,
        meta: {
          jobTitle: form.jobTitle.trim(),
          companyName: form.companyName.trim(),
          hiringManagerName: form.hiringManagerName.trim(),
        },
        personalInfo,
        // Stash the form state so "Edit form" preserves it.
        formState: { form, personalInfo, resumeChoice, uploadedFile, uploadedText },
      });
    } catch (err) {
      setError(err.message || 'Failed to generate cover letter.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="pane">
      <div className="pane-eyebrow">
        <MailIcon size={11} /> Cover Letter
      </div>
      <h1 className="pane-title">AI Cover Letter</h1>

      <div className="pane-card">
        <div className="pane-row">
          <div className="pane-field">
            <label className="pane-label">Job title *</label>
            <input className="pane-input" placeholder="e.g. Senior Software Engineer" value={form.jobTitle} onChange={update('jobTitle')} />
          </div>
          <div className="pane-field">
            <label className="pane-label">Company</label>
            <input className="pane-input" placeholder="e.g. Twilio" value={form.companyName} onChange={update('companyName')} />
          </div>
        </div>

        <div className="pane-field">
          <label className="pane-label">Hiring manager (optional)</label>
          <input className="pane-input" placeholder="e.g. Jane Smith" value={form.hiringManagerName} onChange={update('hiringManagerName')} />
        </div>

        <div className="pane-field">
          <label className="pane-label">Job description *</label>
          <textarea
            className="pane-textarea"
            placeholder="Paste the JD. The more detail, the more targeted the letter."
            value={form.jobDescription}
            onChange={update('jobDescription')}
          />
        </div>

        <div className="pane-field">
          <label className="pane-label">Resume *</label>
          {/* Side-by-side cards in a 2-col grid to save vertical space —
              matches the AI Interview Assistant / Mock / Questions panes. */}
          <div className="es-resume-choices">
            <ResumeChoiceCard
              checked={resumeChoice === 'profile'}
              disabled={!profile.hasResume}
              onClick={() => profile.hasResume && setResumeChoice('profile')}
              label="Use my profile resume"
              status={
                profile.loading ? <span style={{ color: 'var(--es-text-subtle)' }}>(checking…)</span>
                : profile.hasResume ? (
                  <span className="es-choice-status ok"><CheckCircleIcon size={12} /> {profile.data?.resumeFilename}</span>
                ) : (
                  <span className="es-choice-status warn"><AlertTriangleIcon size={12} /> no resume on profile</span>
                )
              }
            />
            <ResumeChoiceCard
              checked={resumeChoice === 'upload'}
              onClick={() => setResumeChoice('upload')}
              label="Upload a different resume"
              status={uploadedFile && (
                <span className="es-choice-status ok">
                  <CheckCircleIcon size={12} /> {uploadedFile.name}
                  {isParsing && <span style={{ color: 'var(--es-text-subtle)', marginLeft: 6 }}>parsing…</span>}
                </span>
              )}
              action={resumeChoice === 'upload' && (
                <button type="button" className="es-choice-action" onClick={() => fileRef.current?.click()}>
                  {uploadedFile ? 'Replace file' : 'Choose file'}
                </button>
              )}
            />
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" onChange={onFile} style={{ display: 'none' }} />
          {parseError && <div className="pane-error">{parseError}</div>}
        </div>
      </div>

      {/* Personal info — pre-filled from the profile, editable for one-off overrides. */}
      <div className="pane-card">
        <div className="clp-section-head">
          <span className="pane-label">Your details (used in the letter header & signature)</span>
        </div>
        <div className="pane-row">
          <div className="pane-field">
            <label className="pane-label">Full name</label>
            <input className="pane-input" placeholder="Jane Doe" value={personalInfo.fullName} onChange={updatePI('fullName')} />
          </div>
          <div className="pane-field">
            <label className="pane-label">Email</label>
            <input className="pane-input" placeholder="jane@email.com" value={personalInfo.email} onChange={updatePI('email')} type="email" />
          </div>
        </div>
        <div className="pane-row">
          <div className="pane-field">
            <label className="pane-label">Phone</label>
            <input className="pane-input" placeholder="(555) 555-1234" value={personalInfo.phone} onChange={updatePI('phone')} />
          </div>
          <div className="pane-field">
            <label className="pane-label">Location</label>
            <input className="pane-input" placeholder="San Francisco, CA" value={personalInfo.location} onChange={updatePI('location')} />
          </div>
        </div>
        <div className="pane-field">
          <label className="pane-label">LinkedIn</label>
          <input className="pane-input" placeholder="linkedin.com/in/janedoe" value={personalInfo.linkedIn} onChange={updatePI('linkedIn')} />
        </div>
      </div>

      {error && <div className="pane-error">{error}</div>}

      <button
        type="button"
        className="pane-cta"
        onClick={handleGenerate}
        disabled={!canStart || generating || gate.locked}
        title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
      >
        {generating
          ? 'Generating cover letter…'
          : <>Generate Cover Letter <SparklesIcon size={13} /> <FreeCreditsLabel feature="coverLetter" /></>}
      </button>
    </div>
  );
}

function ResumeChoiceCard({ checked, disabled, onClick, label, status, action }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`es-choice ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <span className="es-choice-radio" aria-hidden="true">
        <span className="es-choice-radio-dot" />
      </span>
      <div className="es-choice-body">
        <div className="es-choice-label">{label}</div>
        {status && <div className="es-choice-meta">{status}</div>}
        {action && <div className="es-choice-actions">{action}</div>}
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────── */
/* ResultsView                                                             */
/* ─────────────────────────────────────────────────────────────────────── */

function ResultsView({ data, onEdit, onReset, onUpdate }) {
  const { coverLetter, keywords = [], meta = {}, personalInfo = {} } = data || {};
  const [draft, setDraft] = useState(coverLetter || '');
  const [toast, setToast] = useState('');

  // Keep the draft in sync if the parent updates it (e.g. after another generation).
  useEffect(() => { setDraft(coverLetter || ''); }, [coverLetter]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 1800);
  };

  const handleChange = (e) => {
    setDraft(e.target.value);
    onUpdate?.(e.target.value);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      showToast('Copied to clipboard');
    } catch {
      showToast('Could not copy to clipboard');
    }
  };

  const handleDownload = async () => {
    try {
      const docChildren = [];
      const lines = draft.split('\n');
      let i = 0;

      // Lead with full name as the title (centered + bold).
      while (i < lines.length && !lines[i].trim()) i++;
      if (i < lines.length) {
        docChildren.push(new Paragraph({
          children: [new TextRun({ text: lines[i].trim(), font: 'Times New Roman', size: 36, bold: true })],
          spacing: { after: 120, line: 360 },
          alignment: AlignmentType.CENTER,
        }));
        i++;
      }
      // Optional contact line — second non-empty line, centered.
      while (i < lines.length && !lines[i].trim()) i++;
      if (i < lines.length) {
        docChildren.push(new Paragraph({
          children: [new TextRun({ text: lines[i].trim(), font: 'Times New Roman', size: 24 })],
          spacing: { after: 360, line: 360 },
          alignment: AlignmentType.CENTER,
        }));
        i++;
      }

      for (; i < lines.length; i++) {
        const t = lines[i].trim();
        if (!t) continue;
        if (t.toLowerCase().startsWith('sincerely')) {
          docChildren.push(new Paragraph({
            children: [new TextRun({ text: t, font: 'Times New Roman', size: 24 })],
            spacing: { before: 240, after: 0, line: 360 },
            alignment: AlignmentType.LEFT,
          }));
          if (personalInfo.fullName) {
            docChildren.push(new Paragraph({
              children: [new TextRun({ text: personalInfo.fullName, font: 'Times New Roman', size: 24 })],
              spacing: { before: 0, line: 360 },
              alignment: AlignmentType.LEFT,
            }));
          }
          break;
        }
        docChildren.push(new Paragraph({
          children: [new TextRun({ text: t, font: 'Times New Roman', size: 24 })],
          spacing: { after: 180, line: 360 },
          alignment: AlignmentType.LEFT,
        }));
      }

      const doc = new Document({
        sections: [{
          properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
          children: docChildren,
        }],
      });
      const blob = await Packer.toBlob(doc);
      const fileName = `Cover_Letter_${(meta.jobTitle || 'Position').replace(/\s+/g, '_')}_${(meta.companyName || 'Company').replace(/\s+/g, '_')}.docx`;
      saveAs(blob, fileName);
      showToast('DOCX downloaded');
    } catch (err) {
      console.error('DOCX export failed', err);
      showToast('Could not generate DOCX');
    }
  };

  // Highlighted preview — wraps each keyword in a <mark> element.
  const previewHTML = useMemo(() => {
    if (!draft) return '';
    let html = escapeHtml(draft);
    keywords.forEach(k => {
      if (!k) return;
      const safe = escapeRegex(k);
      html = html.replace(new RegExp(`\\b${safe}\\b`, 'gi'), m => `<mark class="clp-mark">${m}</mark>`);
    });
    return html.replace(/\n/g, '<br/>');
  }, [draft, keywords]);

  const wordCount = (draft.match(/\S+/g) || []).length;

  return (
    <div className="clp-root">
      <div className="clp-summary">
        <div className="clp-summary-head">
          <div>
            <div className="pane-eyebrow"><MailIcon size={11} /> Generated</div>
            <h1 className="pane-title" style={{ marginTop: 8 }}>
              Cover Letter for {meta.jobTitle || 'this role'}
            </h1>
            <p className="pane-subtitle" style={{ marginBottom: 0 }}>
              {meta.companyName ? `${meta.companyName} · ` : ''}
              {wordCount} words · editable below
            </p>
          </div>
          <div className="clp-summary-actions">
            <button type="button" className="pane-cta-secondary" onClick={onEdit}>
              Edit form
            </button>
            <button type="button" className="pane-cta-secondary" onClick={onReset}>
              Start over
            </button>
            <button type="button" className="pane-cta-secondary" onClick={handleCopy}>
              <LinkIcon size={13} /> Copy
            </button>
            <button type="button" className="pane-cta" onClick={handleDownload}>
              <SendIcon size={13} /> Download DOCX
            </button>
          </div>
        </div>

        {keywords.length > 0 && (
          <div className="clp-kwbar">
            <span className="clp-kwbar-label">Targeting:</span>
            {keywords.map(k => <span key={k} className="clp-kw">{k}</span>)}
          </div>
        )}
      </div>

      <div className="clp-workspace">
        {/* Editable raw text */}
        <div className="clp-pane">
          <div className="clp-pane-head">
            <span className="clp-pane-label">Editor</span>
          </div>
          <textarea
            className="clp-editor"
            value={draft}
            onChange={handleChange}
            spellCheck
          />
        </div>

        {/* Live preview with keyword highlighting */}
        <div className="clp-pane">
          <div className="clp-pane-head">
            <span className="clp-pane-label">Preview</span>
            <span className="clp-pane-meta">Keywords highlighted</span>
          </div>
          <div className="clp-preview" dangerouslySetInnerHTML={{ __html: previewHTML }} />
        </div>
      </div>

      {toast && <div className="iqp-toast">{toast}</div>}
    </div>
  );
}

/* ─── tiny helpers ─────────────────────────────────────────────────────── */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
